/**
 * Sliding Tiles (Puzzle Master) scoring + manual-run helpers.
 *
 * Manual score is *current board correctness* (recomputed each user move;
 * not a lifetime latch). If a tile leaves its slot, those points drop;
 * if it returns, they come back. HUD = how correct the board is now,
 * weighted toward higher tile numbers.
 *
 *   manualPlacementScore(board)
 *     tile n in its goal cell (index n-1) → n² points
 *     empty/hole in the last cell          → +1
 *     otherwise 0 for that slot
 *   Empty/scrambled boards score 0 (or 1 if only the hole is home).
 *   Solved 4×4: 1²+…+15² + 1 = 1240 + 1 = 1241 (max).
 *
 * Elapsed time is displayed on the HUD but is NOT folded into the live score.
 *
 * Time bonus (human finish only), inversely proportional to time:
 *   puzzleTimeBonus(elapsedMs)
 *     = min(TIME_BONUS_MAX, round(TIME_BONUS_C / max(elapsedSeconds, 1)))
 *   TIME_BONUS_C = 1800, TIME_BONUS_MAX = 300
 *     1s  → 300 (capped; 1800/1 would be absurd)
 *     10s → 180
 *     60s → 30
 *
 * finalScore(manual, elapsedMs, { finishedBy: 'human'|'ai'|'abandon' })
 *   human    → manual + time bonus
 *   ai/abandon → manual only (bonus 0); puzzle was not finished by the user.
 *
 * AI Solve is a quit: HUD + DB `score` freeze at the pre-AI placement value.
 * `history` still includes later AI playback ticks (`action: "ai"`).
 */

export const IDLE_ABANDON_MS = 60_000;
export const IDLE_ABANDON_MESSAGE = 'Game abandoned due to idleness';

/** Empty / hole marker — same as puzzleMasterSolver.EMPTY_CELL. */
export const EMPTY_CELL = null;

/** Scale for inverse time bonus: bonus ≈ C / seconds (then capped). */
export const TIME_BONUS_C = 1800;
/** Cap so a 1-second solve is a meaningful add-on, not 1800 extra points. */
export const TIME_BONUS_MAX = 300;

/** Goal index of numbered tile n is n-1 (row-major). */
export function goalIndex(tile) {
    return tile - 1;
}

/**
 * Placement points for the current board. Not latched: call again after
 * every user move so the HUD tracks tiles that are in (or have left) place.
 *
 * @param {Array} board row-major tiles; `null` is the hole
 * @returns {number}
 */
export function manualPlacementScore(board) {
    if (!Array.isArray(board) || board.length < 2) return 0;
    const n = board.length;
    let score = 0;
    for (let tile = 1; tile < n; tile += 1) {
        if (board[goalIndex(tile)] === tile) score += tile * tile;
    }
    if (board[n - 1] === EMPTY_CELL) score += 1;
    return score;
}

/**
 * One-time inverse time bonus. Call only when composing a human-finish score;
 * the live HUD never uses this while the puzzle is in progress.
 */
export function puzzleTimeBonus(elapsedMs) {
    const ms = Number(elapsedMs);
    if (!Number.isFinite(ms) || ms < 0) return 0;
    const elapsedSeconds = Math.max(ms / 1000, 1);
    return Math.min(TIME_BONUS_MAX, Math.round(TIME_BONUS_C / elapsedSeconds));
}

function normalizeFinishedBy(finishedBy) {
    if (finishedBy === 'human') return 'human';
    if (finishedBy === 'ai' || finishedBy === 'ai_solve') return 'ai';
    return 'abandon';
}

/**
 * Compose the upload/HUD final score.
 * @returns {{ score: number, timeBonus: number, manualScore: number, finishedBy: string }}
 */
export function finalScore(manualScore, elapsedMs, { finishedBy } = {}) {
    const n = Number(manualScore);
    const manual = Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
    const by = normalizeFinishedBy(finishedBy);
    if (by !== 'human') {
        return { score: manual, timeBonus: 0, manualScore: manual, finishedBy: by };
    }
    const timeBonus = puzzleTimeBonus(elapsedMs);
    return {
        score: manual + timeBonus,
        timeBonus,
        manualScore: manual,
        finishedBy: by,
    };
}

/** HUD score: frozen pre-AI/abandon value, otherwise live placement score. */
export function displayedPuzzleScore({ scoreFrozen = false, scoreAtQuit, board } = {}) {
    if (scoreFrozen && scoreAtQuit != null) return scoreAtQuit;
    return manualPlacementScore(board);
}

/** Snapshot score/moves at AI Solve click; later AI ticks must not change them. */
export function freezeScoreAtQuit(state) {
    const userMoves = state?.moves ?? 0;
    const score = manualPlacementScore(state?.board);
    return {
        ...state,
        userMoves,
        moves: userMoves,
        score,
        displayScore: score,
        scoreAtQuit: score,
        scoreFrozen: true,
    };
}

/**
 * Simulated AI playback step: history may grow, score and user-move count stay frozen.
 */
export function applyAiPlaybackTick(state, tick) {
    if (!state) return state;
    const score = state.scoreAtQuit ?? state.score;
    const userMoves = state.userMoves ?? state.moves;
    return {
        ...state,
        history: [...(state.history || []), tick],
        score,
        displayScore: score,
        moves: userMoves,
        userMoves,
        scoreFrozen: true,
    };
}

/** Tick shape for user slides and AI playback steps (replay uses `tile`). */
export function puzzleHistoryTick(tile, action = 'user', board = null) {
    const tick = { action, tile };
    if (Array.isArray(board)) {
        tick.puzzle = board.slice();
        tick.score = manualPlacementScore(board);
    }
    return tick;
}

export function isPuzzleTimerRunning({
    isGameStarted = false,
    isGameWon = false,
    isAbandoned = false,
    timerFrozen = false,
    autoPlay = false,
    isPaused = false,
} = {}) {
    return Boolean(
        isGameStarted
        && !isGameWon
        && !isAbandoned
        && !timerFrozen
        && !autoPlay
        && !isPaused
    );
}

/**
 * Idle applies only while waiting on the user after Start — not in the menu,
 * after the run is over, or while AI is solving/animating.
 */
export function isIdleAbandoned({
    lastUserInputAt,
    now,
    gameInProgress = false,
    awaitingUserInput = true,
    alreadyOver = false,
    timeoutMs = IDLE_ABANDON_MS,
} = {}) {
    if (!gameInProgress || !awaitingUserInput || alreadyOver) return false;
    if (lastUserInputAt == null || !Number.isFinite(lastUserInputAt)) return false;
    return now - lastUserInputAt >= timeoutMs;
}

export function applyIdleTick(state, now, timeoutMs = IDLE_ABANDON_MS) {
    if (!state || state.abandoned || state.alreadyOver) return state;
    if (!isIdleAbandoned({
        lastUserInputAt: state.lastUserInputAt,
        now,
        gameInProgress: state.gameInProgress,
        awaitingUserInput: state.awaitingUserInput,
        alreadyOver: state.alreadyOver,
        timeoutMs,
    })) {
        return state;
    }
    const next = {
        ...state,
        abandoned: true,
        alreadyOver: true,
        gameInProgress: false,
        timerStopped: true,
        message: IDLE_ABANDON_MESSAGE,
        shouldUpload: !state.uploaded,
    };
    return next;
}

export function markIdleUploaded(state) {
    if (!state) return state;
    return { ...state, uploaded: true, shouldUpload: false };
}

export function shouldUploadIdleAbandon({ abandoned = false, alreadyUploaded = false } = {}) {
    return Boolean(abandoned && !alreadyUploaded);
}

export function humanFinishUploadArgs({ board, moves, elapsedMs } = {}) {
    const manualScore = manualPlacementScore(board);
    const { score, timeBonus } = finalScore(manualScore, elapsedMs, { finishedBy: 'human' });
    return {
        score,
        extra: {
            finished_by: 'human',
            moves_count: moves,
            elapsed_time: elapsedMs,
            manual_score: manualScore,
            time_bonus: timeBonus,
        },
    };
}

/**
 * Args for `finalizeAndUpload` after AI playback ends.
 * Score stays at the quit-moment board (manual only, no time bonus).
 */
export function aiSolveUploadArgs({ board, userMoves, elapsedAtQuit, aiFinished = true } = {}) {
    const manualScore = manualPlacementScore(board);
    const { score, timeBonus } = finalScore(manualScore, elapsedAtQuit, { finishedBy: 'ai' });
    return {
        score,
        extra: {
            quit_reason: 'ai_solve',
            finished_by: 'ai',
            score_at_quit: score,
            moves_count: userMoves,
            elapsed_time: elapsedAtQuit,
            manual_score: manualScore,
            time_bonus: timeBonus,
            ai_finished: Boolean(aiFinished),
        },
    };
}

export function idleAbandonUploadArgs({ board, moves, elapsedMs } = {}) {
    const manualScore = manualPlacementScore(board);
    const { score, timeBonus } = finalScore(manualScore, elapsedMs, { finishedBy: 'abandon' });
    return {
        score,
        extra: {
            quit_reason: 'idle',
            finished_by: 'abandon',
            score_at_quit: score,
            moves_count: moves,
            elapsed_time: elapsedMs,
            manual_score: manualScore,
            time_bonus: timeBonus,
        },
    };
}
